// Tela da Tecnologia na Visão Geral (#6504): Victor e Benevides. Abre no mês, com seletor.
//
// Pergunta da tela: as demandas estão no prazo?
//   - hoje: minhas demandas (urgentes, vencendo hoje, atrasadas), o cronômetro rodando e a
//     fila de Tecnologia sem responsável (Central de Solicitações);
//   - mês: demandas no prazo da prioridade (meta 90%), retrabalho (até 3%) e tarefas de
//     projeto no prazo (meta 90%), da própria pessoa.
//   - saúde do sistema (#6508): WhatsApp, robô da Equatorial, e-mail e funções do servidor,
//     logo depois do "Hoje".
import React, { useMemo } from 'react';
import { FolderKanban, LifeBuoy } from 'lucide-react';
import { entregasDoPeriodo } from '../../../src/lib/visaoGeral/tecnologia';
import type { MinhasTarefas } from '../../../src/lib/visaoGeral/minhasTarefas';
import { BlocosDoDia } from '../BlocosDoDia';
import { BlocoSaudeDoSistema } from '../BlocoSaudeDoSistema';
import { BarraCronometro, CartoesEntregas, GraficoEntregasPorSemana, PainelFilaSemResponsavel, janelaDoMes } from '../blocosTecnologiaMarketing';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, ListaAtalhos, SecaoVisaoGeral } from '../componentes';
import { useMinhasTarefas } from '../useMinhasTarefas';
import { soSolicitacoes } from '../../../src/lib/visaoGeral/minhasTarefas';
import { useDemandasConcluidas, useFilaSemResponsavel } from '../useTecnologiaMarketing';
import { MinhaFila } from './TelaPadrao';
import type { PropsTelaVisaoGeral } from '../tipos';

/** Abertas com prioridade urgência, em qualquer grupo de vencimento. */
export const urgentesAbertas = (m: MinhasTarefas) =>
  [...m.atrasadas, ...m.hoje, ...m.proximas, ...m.depois, ...m.semData].filter(t => t.prioridade === 'urgencia').length;

const TelaTecnologia: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo, aoMudarMes }) => {
  const j = useMemo(() => janelaDoMes(periodo.mes), [periodo.mes]);
  const ids = useMemo(() => [pessoa.id], [pessoa.id]);
  const { dados: todas } = useMinhasTarefas(pessoa.id);
  // Só as solicitações da Central: atividade do CRM (visita, ligação) não é demanda.
  const minhas = useMemo(() => (todas ? soSolicitacoes(todas) : null), [todas]);
  const fila = useFilaSemResponsavel(['Tecnologia']);
  const demandas = useDemandasConcluidas(j.inicio, j.fim, ids);
  const entregas = useMemo(() => (demandas.dados ? entregasDoPeriodo(demandas.dados) : null), [demandas.dados]);

  const acoes = souEu ? <BotaoAcao rotulo="Central de Solicitações" icone={LifeBuoy} para="/tarefas/solicitacoes" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} acoes={acoes} />

      <BlocosDoDia pessoa={pessoa} perfil="tecnologia" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="minhas demandas e a fila da Tecnologia">
        <CartaoHoje valor={minhas ? urgentesAbertas(minhas) : null} rotulo="urgências abertas" tom="critico" para="/tarefas/solicitacoes"
          dica="Demandas suas com prioridade urgência ainda abertas (prazo de 1 dia útil)." />
        <CartaoHoje valor={minhas ? minhas.hoje.length : null} rotulo="vencem hoje" tom="atencao" para="/tarefas/solicitacoes" />
        <CartaoHoje valor={minhas ? minhas.atrasadas.length : null} rotulo="atrasadas" tom={minhas && minhas.atrasadas.length ? 'critico' : 'ok'} para="/tarefas/solicitacoes"
          dica="Abertas com o vencimento já passado." />
        <CartaoHoje valor={fila.dados ? fila.dados.length : null} rotulo="na fila sem responsável" tom="neutro" para="/tarefas/solicitacoes"
          dica="Solicitações para Tecnologia que ninguém pegou ainda." />
      </SecaoVisaoGeral>
      <BarraCronometro pessoaId={pessoa.id} />

      <BlocoSaudeDoSistema />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MinhaFila dados={minhas} titulo="Minhas demandas" />
        <PainelFilaSemResponsavel fila={fila.dados} titulo="Fila sem responsável" />
      </div>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${j.rotulo}, demandas concluídas por ${(pessoa.full_name || '').split(' ')[0] || 'você'}`} colunas={3}>
        <CartoesEntregas e={entregas} quem="por você" />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <GraficoEntregasPorSemana demandas={demandas.dados} titulo="Minhas entregas por semana" />
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Central de Solicitações', para: '/tarefas/solicitacoes', icone: LifeBuoy },
        { rotulo: 'Projetos', para: '/projetos', icone: FolderKanban },
      ]} />
    </div>
  );
};

export default TelaTecnologia;
